<template>
  <div class="information-section">
    <b-row class="d-flex align-items-center mb-2 mt-1 px-2">
      <b-col>
        <span class="ap-title ap-title__main">{{ $tc('globalLocator.sections.requestData') }}</span>
      </b-col>
    </b-row>

    <b-row class="px-2 pt-2">
      <b-col cols="3">
        <div class="info-card d-flex flex-column justify-content-center align-items-center">
          <section-info-col-type-date
            :label="$tc('globalLocator.labels.requestDate')"
            :date="request.requestDate"
            col-class="flex-column-center"
          />
        </div>
      </b-col>
      <b-col>
        <div class="info-card d-flex flex-column justify-content-center align-items-center">
          <section-info-col-type
            :label="$tc('globalLocator.labels.requestingProfessional')"
            :data="request.administrativePetitioner"
            col-class="flex-column-center"
          >
            <template #default="{ data }">
              {{ data.fullName }}
            </template>
          </section-info-col-type>
        </div>
      </b-col>
      <b-col>
        <div class="info-card d-flex flex-column justify-content-center align-items-center">
          <section-info-col-type
            :label="$tc('globalLocator.labels.requestingUnit')"
            :data="request.physicianDepartment"
            col-class="flex-column-center"
          >
            <template #default="{ data }">
              {{ data.name }}
            </template>
          </section-info-col-type>
        </div>
      </b-col>
    </b-row>

    <b-row class="px-2 pt-4">
      <b-col
        cols="auto"
        class="d-flex align-items-center"
      >
        <span class="ap-title ap-title__divider">{{ $tc('globalLocator.labels.requester') }}</span>
      </b-col>
      <b-col>
        <hr>
      </b-col>
    </b-row>

    <b-row class="px-2 mt-1">
      <b-col cols="4">
        <section-info-col-type
          :label="$tc('globalLocator.labels.cenPhysName')"
          :data="request.physicianDepartment"
        >
          <template #default="{ data }">
            {{ data.center.name }}
          </template>
        </section-info-col-type>
      </b-col>
      <b-col cols="4">
        <section-info-col-type
          :label="$tc('globalLocator.labels.unit')"
          :data="request.physicianDepartment"
        >
          <template #default="{ data }">
            {{ data.name }}
          </template>
        </section-info-col-type>
      </b-col>
      <b-col cols="4">
        <section-info-col-type-personnel
          :label="$tc('globalLocator.labels.professional')"
          :personnel="request.physicianPetitioner"
        />
      </b-col>
    </b-row>

    <b-row class="px-2 pt-4">
      <b-col
        cols="auto"
        class="d-flex align-items-center"
      >
        <span class="ap-title ap-title__divider">{{ $tc('globalLocator.labels.destination') }}</span>
      </b-col>
      <b-col>
        <hr>
      </b-col>
    </b-row>

    <b-row class="px-2 mt-1">
      <b-col cols="4">
        <section-info-col-type
          :label="$tc('globalLocator.labels.cenPhysName')"
          :data="request.destinationDepartment"
        >
          <template #default="{ data }">
            {{ data.center.name }}
          </template>
        </section-info-col-type>
      </b-col>
      <b-col cols="4">
        <section-info-col-type
          :label="$tc('globalLocator.labels.unit')"
          :data="request.destinationDepartment"
        >
          <template #default="{ data }">
            {{ data.name }}
          </template>
        </section-info-col-type>
      </b-col>
      <b-col cols="4">
        <section-info-col-type
          :label="$tc('globalLocator.labels.receiver')"
          :data="request.receiver"
        >
          <template #image>
            <vt-personnel-profile-image
              :personnel-id="request.receiver && request.receiver.id"
              img-class="photo mr-2"
            />
          </template>

          <template #default="{ data: personnel }">
            {{ personnel }}
          </template>
        </section-info-col-type>
      </b-col>
    </b-row>

    <b-row class="px-2 mt-3">
      <b-col>
        <hr>
      </b-col>
    </b-row>

    <b-row class="px-2 mt-1">
      <b-col cols="3">
        <section-info-col-type
          :label="$tc('globalLocator.labels.laboratory')"
          :data="request.laboratory"
        >
          <template #default="{ data }">
            {{ data.labName }}
          </template>
        </section-info-col-type>
      </b-col>
      <b-col cols="3">
        <section-info-col-type
          :label="$tc('globalLocator.labels.regime')"
          :data="request.regime"
        >
          <template #default="{ data }">
            {{ data.name }}
          </template>
        </section-info-col-type>
      </b-col>
      <b-col cols="3">
        <section-info-col-type
          :label="$tc('globalLocator.labels.episode')"
          :data="request.episode"
        />
      </b-col>
      <b-col cols="3">
        <section-info-col-type
          :label="$tc('globalLocator.labels.caseLabel')"
          :data="request.caseStudy && request.caseStudy.label"
        />
      </b-col>
      <b-col
        cols="12"
        class="mt-3"
      >
        <section-info-col-type
          :label="$tc('globalLocator.labels.relevantClinicalData')"
          :data="request.clinicalData"
        />
      </b-col>
    </b-row>

    <b-row class="px-2 mt-1">
      <b-col
        cols="12"
        class="mt-3"
      >
        <b-tabs class="vt-tabs inside-tab">
          <b-tab
            v-for="(subrequest, keySubrequests) in request.subrequests"
            :id="'samplesCollapsableCard'"
            :key="keySubrequests"
            :title="subrequest.externalId"
          >
            <div class="sr-detail-block">
              <b-row class="tab-box px-3">
                <b-col class="form-box">
                  <div class="structure pt-2 pl-4 pr-4 pb-2 mt-3">
                    <h5 class="main">
                      <i
                        class="far fa-list mr-2"
                        aria-hidden="true"
                      />{{ $tc('global.sampleData') }}
                    </h5>
                    <b-row>
                      <b-col
                        v-if="subrequest.topographicCode"
                        cols="3"
                        class="my-2"
                      >
                        <div class="data-card d-flex align-items-center">
                          <div class="data-card-text d-flex flex-column">
                            <small>{{ $tc('globalLocator.labels.location') }}</small>
                            <span>{{ subrequest.topographicCode.description }}</span>
                          </div>
                        </div>
                      </b-col>
                      <b-col
                        v-if="subrequest.sampleType"
                        cols="3"
                        class="my-2"
                      >
                        <div class="data-card d-flex align-items-center">
                          <div class="data-card-text d-flex flex-column">
                            <small>{{ $tc('globalLocator.labels.sampleType') }}</small>
                            <span>{{ subrequest.sampleType.description }}</span>
                          </div>
                        </div>
                      </b-col>
                      <b-col
                        v-if="subrequest.procedure"
                        cols="3"
                        class="my-2"
                      >
                        <div class="data-card d-flex align-items-center">
                          <div class="data-card-text d-flex flex-column">
                            <small>{{ $tc('globalLocator.labels.procedure') }}</small>
                            <span>{{ subrequest.procedure.description }}</span>
                          </div>
                        </div>
                      </b-col>
                      <b-col
                        v-if="subrequest.mostLikelyMorphologicCode"
                        cols="3"
                        class="my-2"
                      >
                        <div class="data-card d-flex align-items-center">
                          <div class="data-card-text d-flex flex-column">
                            <small>{{ $tc('globalLocator.labels.probableDiagnosis') }}</small>
                            <span>{{ subrequest.mostLikelyMorphologicCode.description }}</span>
                          </div>
                        </div>
                      </b-col>
                      <b-col
                        cols="3"
                        class="my-2"
                      >
                        <div class="data-card d-flex align-items-center">
                          <div class="data-card-text d-flex flex-column">
                            <small>{{ $tc('globalLocator.labels.fragments') }}</small>
                            <span>{{ subrequest.fragments }}</span>
                          </div>
                        </div>
                      </b-col>
                      <b-col
                        cols="3"
                        class="my-2"
                      >
                        <div class="data-card d-flex align-items-center">
                          <div class="data-card-text d-flex flex-column">
                            <small>{{ $tc('globalLocator.labels.conservationMedium') }}</small>
                            <span>{{ subrequest.conservation }}</span>
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                </b-col>
              </b-row>
              <b-row class="tab-box px-3">
                <b-col class="form-box">
                  <div class="structure pt-2 pl-4 pr-4 pb-2">
                    <h5 class="main">
                      <i
                        class="far fa-info-circle mr-2"
                        aria-hidden="true"
                      />{{ $tc('global.supplementaryData') }}
                    </h5>
                    <b-row>
                      <b-col
                        v-for="(info, keySubrequestinformations) in subrequest.subrequestinformations"
                        :key="keySubrequestinformations"
                        cols="3"
                        class="my-2"
                      >
                        <div class="data-card d-flex align-items-center">
                          <div class="data-card-text d-flex flex-column">
                            <small>{{ info.formQuestion.question.queQuestion }}</small>
                            <span>{{ info.sriAnswer }}</span>
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col
                        v-for="(info, keyAdditionalInformation) in subrequest.additionalInformation"
                        :key="keyAdditionalInformation"
                        cols="3"
                        class="my-2"
                      >
                        <div class="data-card d-flex align-items-center">
                          <div class="data-card-text d-flex flex-column">
                            <small>{{ info.seiTitle }}</small>
                            <span>{{ info.seiAnswer }}</span>
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                </b-col>
              </b-row>
            </div>
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import SectionInfoColType from '@/modules/globalLocatorModule/sections/info/col-types/section-info-col-type.vue'
import SectionInfoColTypeDate from '@/modules/globalLocatorModule/sections/info/col-types/date/section-info-col-type-date.vue'
import SectionInfoColTypePersonnel from '@/modules/globalLocatorModule/sections/info/col-types/personnel/section-info-col-type-personnel.vue'

export default {
    name: 'RequestInfoSection',
    components: {
        SectionInfoColType,
        SectionInfoColTypeDate,
        SectionInfoColTypePersonnel
    },
    props: {
        request: { type: Object, required: true }
    }
}
</script>

<style lang="scss" scoped>
    ::v-deep {
        .photo {
            width: 30px;
            height: 30px;
            border-radius: 50%;
        }
    }

    .data-card {
        .data-card-text {
            line-height: 14px;

            span {
                color: vt-color(dark-text);
                font-size: font-size(very-small);
            }

            small {
                font-size: font-size(very-tiny);
            }
        }
    }
</style>
